import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './client'
import type {
  BlacklistedDomain,
  BlacklistedProxy,
  BookingEngineDecision,
  BrokenBookingEngine,
  ChannelMetrics,
  CockpitConfig,
  CompetitorMetrics,
  FailedJob,
  HostUsage,
  HostUsagePoint,
  HotelConfigurationSummary,
  HotelMetrics,
  IpUsage,
  JobHistoryEntry,
  JobRunResult,
  LogEntry,
  LogProject,
  LogSearchRequest,
  MissingPlanning,
  NextScreening,
  PosScript,
  PosScriptTestRequest,
  PosScriptTestResponse,
  PriceRecommendationMetrics,
  ProductionResource,
  ProductMetrics,
  QueueMessageType,
  ResendPlanningResult,
  ScheduledJob,
  ScheduledJobInput,
  SchedulerSummary,
  ScrapingRatio,
  SentinelChannel,
  SentinelCompetitor,
  SentinelHotel,
  SentinelMetrics,
  SettingsRow,
  SettingsTableDescription,
  SettingsTableSummary,
  SynchronizeResult,
  TokenAssignment,
} from './types'

// One hook per endpoint. Dashboard queries refresh through the global RefreshProvider.

export const useConfig = () =>
  useQuery({ queryKey: ['config'], queryFn: () => api.get<CockpitConfig>('/config'), staleTime: Infinity })

// RateScreener
export const useRateScreenerMetrics = () =>
  useQuery({ queryKey: ['ratescreener', 'metrics'], queryFn: () => api.get<ProductMetrics | null>('/ratescreener/metrics') })

export const useChannels = (hours: number) =>
  useQuery({ queryKey: ['ratescreener', 'channels', hours], queryFn: () => api.get<ChannelMetrics[]>('/ratescreener/channels', { hours }) })

export const useChannelHotels = (channelId: number, bookingEngine: string | undefined, hours: number) =>
  useQuery({
    queryKey: ['ratescreener', 'hotels', channelId, bookingEngine, hours],
    queryFn: () => api.get<HotelMetrics[]>(`/ratescreener/channels/${channelId}/hotels`, { bookingEngine, hours }),
  })

export const useHotelCompetitors = (channelId: number, bookingEngine: string | undefined, hotelId: number, hours: number, enabled: boolean) =>
  useQuery({
    queryKey: ['ratescreener', 'competitors', channelId, bookingEngine, hotelId, hours],
    queryFn: () => api.get<CompetitorMetrics[]>(`/ratescreener/channels/${channelId}/hotels/${hotelId}/competitors`, { bookingEngine, hours }),
    enabled,
  })

export const useSynchronizeChannel = () =>
  useMutation({
    mutationFn: ({ channelId, bookingEngine }: { channelId: number; bookingEngine?: string }) =>
      api.post<SynchronizeResult>(`/ratescreener/channels/${channelId}/synchronize`, undefined, { bookingEngine }),
  })

export const useMissingPlannings = () =>
  useQuery({ queryKey: ['ratescreener', 'missing-plannings'], queryFn: () => api.get<MissingPlanning[]>('/ratescreener/missing-plannings') })

export const useRebuildPlannings = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (plannings: MissingPlanning[]) =>
      api.post<ResendPlanningResult[]>(
        '/ratescreener/missing-plannings/rebuild',
        plannings.map((p) => ({ hotelId: p.hotelId, startDate: p.startDate, numberOfDays: p.numberOfDays, synchronizationId: p.synchronizationId })),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ratescreener', 'missing-plannings'] }),
  })
}

export const useNextScreening = () =>
  useQuery({ queryKey: ['ratescreener', 'next-screening'], queryFn: () => api.get<NextScreening>('/ratescreener/next-screening') })

export const useScrapingRatios = (hours: number, enabled = true) =>
  useQuery({ queryKey: ['ratescreener', 'scraping-ratios', hours], queryFn: () => api.get<ScrapingRatio[]>('/ratescreener/scraping-ratios', { hours }), enabled })

export const usePointsOfSale = (enabled = true) =>
  useQuery({ queryKey: ['ratescreener', 'pos'], queryFn: () => api.get<Record<string, string[]>>('/ratescreener/pos'), enabled })

export const usePosCompetitors = () =>
  useMutation({ mutationFn: ({ posId, posName }: { posId: number; posName: string }) => api.get<number[]>(`/ratescreener/pos/${posId}/${encodeURIComponent(posName)}/competitors`) })

// Sentinel
export const useSentinelMetrics = () =>
  useQuery({ queryKey: ['sentinel', 'metrics'], queryFn: () => api.get<SentinelMetrics>('/sentinel/metrics') })

export const useSentinelChannels = () =>
  useQuery({ queryKey: ['sentinel', 'channels'], queryFn: () => api.get<SentinelChannel[]>('/sentinel/channels') })

export const useSentinelHotels = (channelId: number) =>
  useQuery({ queryKey: ['sentinel', 'hotels', channelId], queryFn: () => api.get<SentinelHotel[]>(`/sentinel/channels/${channelId}/hotels`) })

export const useSentinelCompetitors = (channelId: number, customerId: number, enabled: boolean) =>
  useQuery({
    queryKey: ['sentinel', 'competitors', channelId, customerId],
    queryFn: () => api.get<SentinelCompetitor[]>(`/sentinel/channels/${channelId}/hotels/${customerId}/competitors`),
    enabled,
  })

// Proxies
export const useBlacklistSummary = () =>
  useQuery({ queryKey: ['proxies', 'blacklist-summary'], queryFn: () => api.get<BlacklistedDomain[]>('/proxies/blacklisted/summary') })

export const useBlacklisted = (enabled: boolean) =>
  useQuery({ queryKey: ['proxies', 'blacklisted'], queryFn: () => api.get<BlacklistedProxy[]>('/proxies/blacklisted'), enabled })

export const useUnblacklist = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ip, domain }: { ip: string; domain: string }) => api.delete<unknown>('/proxies/blacklisted', { ip, domain }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['proxies'] }),
  })
}

export const useHostUsage = () =>
  useQuery({ queryKey: ['proxies', 'host-usage'], queryFn: () => api.get<HostUsage[]>('/proxies/host-usage') })

export const useHostUsageMinutes = (host: string, date: string) =>
  useQuery({ queryKey: ['proxies', 'host-usage-minutes', host, date], queryFn: () => api.get<HostUsagePoint[]>('/proxies/host-usage/minutes', { host, date }), enabled: host.length > 0 })

export const useIpUsage = (enabled: boolean) =>
  useQuery({ queryKey: ['proxies', 'ip-usage'], queryFn: () => api.get<IpUsage[]>('/proxies/ip-usage'), enabled })

// Scheduler
export const useFailedJobs = () =>
  useQuery({ queryKey: ['scheduler', 'failed'], queryFn: () => api.get<FailedJob[]>('/scheduler/failed') })

export const useSchedulerSummary = () =>
  useQuery({ queryKey: ['scheduler', 'summary'], queryFn: () => api.get<SchedulerSummary>('/scheduler/summary') })

export const useJobs = (enabled: boolean) =>
  useQuery({ queryKey: ['scheduler', 'jobs'], queryFn: () => api.get<ScheduledJob[]>('/scheduler/jobs'), enabled })

export const useJobHistory = (id: string | null) =>
  useQuery({ queryKey: ['scheduler', 'history', id], queryFn: () => api.get<JobHistoryEntry[]>(`/scheduler/jobs/${id}/history`), enabled: id !== null })

export const useJobMutations = () => {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['scheduler'] })
  return {
    create: useMutation({ mutationFn: (input: ScheduledJobInput) => api.post<ScheduledJob>('/scheduler/jobs', input), onSuccess: invalidate }),
    update: useMutation({ mutationFn: ({ id, input }: { id: string; input: ScheduledJobInput }) => api.put<ScheduledJob>(`/scheduler/jobs/${id}`, input), onSuccess: invalidate }),
    remove: useMutation({ mutationFn: (id: string) => api.delete<void>(`/scheduler/jobs/${id}`), onSuccess: invalidate }),
    run: useMutation({ mutationFn: (id: string) => api.post<JobRunResult>(`/scheduler/jobs/${id}/run`), onSuccess: invalidate }),
  }
}

// PoS scripts
export const usePosScripts = (enabled: boolean) =>
  useQuery({ queryKey: ['pos-scripts'], queryFn: () => api.get<PosScript[]>('/pos-scripts'), enabled })

export const usePosScriptMutations = () => {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['pos-scripts'] })
  return {
    testRrcRequest: useMutation({ mutationFn: (request: PosScriptTestRequest) => api.post<PosScriptTestResponse>('/pos-scripts/validate/rrc-request', request) }),
    testOnResponse: useMutation({ mutationFn: (request: PosScriptTestRequest) => api.post<PosScriptTestResponse>('/pos-scripts/validate/on-response', request) }),
    save: useMutation({ mutationFn: (request: PosScriptTestRequest) => api.post<PosScriptTestResponse>('/pos-scripts', request), onSuccess: invalidate }),
    restore: useMutation({
      mutationFn: ({ posId, posName }: { posId: number; posName: string }) => api.post<void>(`/pos-scripts/${posId}/${encodeURIComponent(posName)}/restore`),
      onSuccess: invalidate,
    }),
  }
}

// Settings tables
export const useSettingsTables = () =>
  useQuery({ queryKey: ['settings', 'tables'], queryFn: () => api.get<SettingsTableSummary[]>('/settings/tables'), staleTime: Infinity })

export const useSettingsTable = (key: string | null) =>
  useQuery({ queryKey: ['settings', 'table', key], queryFn: () => api.get<SettingsTableDescription>(`/settings/tables/${key}`), enabled: key !== null })

export const useSettingsRows = (key: string | null) =>
  useQuery({ queryKey: ['settings', 'rows', key], queryFn: () => api.get<SettingsRow[]>(`/settings/tables/${key}/rows`), enabled: key !== null })

export const useSettingsRowMutations = (key: string) => {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['settings', 'rows', key] })
  return {
    create: useMutation({ mutationFn: (row: SettingsRow) => api.post<void>(`/settings/tables/${key}/rows`, row), onSuccess: invalidate }),
    update: useMutation({ mutationFn: (row: SettingsRow) => api.put<void>(`/settings/tables/${key}/rows`, row), onSuccess: invalidate }),
    remove: useMutation({ mutationFn: (id: string) => api.delete<void>(`/settings/tables/${key}/rows/${encodeURIComponent(id)}`), onSuccess: invalidate }),
  }
}

// Team, hotels, booking engines, logs, price recommendation
export const useTokenManagers = () =>
  useQuery({ queryKey: ['token-managers'], queryFn: () => api.get<TokenAssignment[]>('/token-managers') })

export const useHotelConfiguration = () =>
  useMutation({ mutationFn: (hotelId: number) => api.get<HotelConfigurationSummary>(`/hotels/${hotelId}/configuration`) })

export const useDisableHotel = () =>
  useMutation({ mutationFn: (hotelId: number) => api.post<HotelConfigurationSummary>(`/hotels/${hotelId}/disable`) })

export const useBrokenBookingEngines = (bookingEngineName: string, enabled: boolean) =>
  useQuery({
    queryKey: ['booking-engines', 'broken', bookingEngineName],
    queryFn: () => api.get<BrokenBookingEngine[]>('/booking-engines/broken', { bookingEngineName }),
    enabled,
  })

export const useBookingEngineDecision = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ entry, decision }: { entry: BrokenBookingEngine; decision: BookingEngineDecision }) =>
      api.post<void>(`/booking-engines/broken/${entry.competitorId}/decision`, {
        decision,
        currentBookingEngineName: entry.currentBookingEngineName,
        newBookingEngineName: entry.newBookingEngineName,
        newBookingEngineParams: entry.newBookingEngineParams,
        hotelWebsite: entry.hotelWebsite,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['booking-engines'] }),
  })
}

export const useLogProjects = () =>
  useQuery({ queryKey: ['logs', 'projects'], queryFn: () => api.get<LogProject[]>('/logs/projects'), staleTime: Infinity })

export const useSearchLogs = () =>
  useMutation({ mutationFn: (request: LogSearchRequest) => api.post<LogEntry[]>('/logs/search', request) })

export const useProductionResources = () =>
  useQuery({ queryKey: ['resources'], queryFn: () => api.get<ProductionResource[]>('/resources'), staleTime: Infinity })

export const useQueueMessageTypes = () =>
  useQuery({ queryKey: ['tools', 'queue-message-types'], queryFn: () => api.get<QueueMessageType[]>('/tools/queue-message-types'), staleTime: Infinity })

export const usePriceRecommendationMetrics = (hours = 24) =>
  useQuery({ queryKey: ['price-recommendation', hours], queryFn: () => api.get<PriceRecommendationMetrics>('/price-recommendation/metrics', { hours }) })

// Queue / instances monitoring. Cards load on demand, so every hook takes `enabled`.
import type { MonitoredProduct, QueueInfo, ScalabilityConfiguration, ScaleHistoryEntry, ServiceInfo } from './types'

type Target = { product: string; environment: string }

export const useMonitoredProducts = () =>
  useQuery({ queryKey: ['monitoring', 'products'], queryFn: () => api.get<MonitoredProduct[]>('/monitoring/products'), staleTime: Infinity })

export const useQueues = ({ product, environment }: Target, enabled: boolean) =>
  useQuery({
    queryKey: ['monitoring', 'queues', product, environment],
    queryFn: () => api.get<QueueInfo[]>(`/monitoring/${product}/${environment}/queues`),
    enabled,
  })

export const useClearQueue = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ product, environment, storage, queue }: Target & { storage: string; queue: string }) =>
      api.delete<void>(`/monitoring/${product}/${environment}/queues/${encodeURIComponent(storage)}/${encodeURIComponent(queue)}/messages`),
    onSuccess: (_, { product, environment }) => queryClient.invalidateQueries({ queryKey: ['monitoring', 'queues', product, environment] }),
  })
}

export const useServices = ({ product, environment }: Target, enabled: boolean) =>
  useQuery({
    queryKey: ['monitoring', 'services', product, environment],
    queryFn: () => api.get<ServiceInfo[]>(`/monitoring/${product}/${environment}/services`),
    enabled,
  })

export const useServiceMutations = () => {
  const queryClient = useQueryClient()
  const invalidate = (_: unknown, { product, environment }: Target) => queryClient.invalidateQueries({ queryKey: ['monitoring', 'services', product, environment] })
  return {
    scale: useMutation({
      mutationFn: ({ product, environment, service, count }: Target & { service: string; count: number }) =>
        api.put<void>(`/monitoring/${product}/${environment}/services/${encodeURIComponent(service)}/instances/${count}`),
      onSuccess: invalidate,
    }),
    reboot: useMutation({
      mutationFn: ({ product, environment, service, instance }: Target & { service: string; instance: string }) =>
        api.post<void>(`/monitoring/${product}/${environment}/services/${encodeURIComponent(service)}/instances/${encodeURIComponent(instance)}/reboot`),
      onSuccess: invalidate,
    }),
  }
}

export const useScalability = ({ product, environment }: Target, service: string | null) =>
  useQuery({
    queryKey: ['monitoring', 'scalability', product, environment, service],
    queryFn: () => api.get<ScalabilityConfiguration>(`/monitoring/${product}/${environment}/services/${encodeURIComponent(service!)}/scalability`),
    enabled: service !== null,
  })

export const useSaveScalability = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ product, environment, service, configuration }: Target & { service: string; configuration: ScalabilityConfiguration }) =>
      api.put<void>(`/monitoring/${product}/${environment}/services/${encodeURIComponent(service)}/scalability`, configuration),
    onSuccess: (_, { product, environment, service }) => queryClient.invalidateQueries({ queryKey: ['monitoring', 'scalability', product, environment, service] }),
  })
}

export const useServiceHistory = ({ product, environment }: Target, service: string | null, hours: number) =>
  useQuery({
    queryKey: ['monitoring', 'history', product, environment, service, hours],
    queryFn: () => api.get<ScaleHistoryEntry[]>(`/monitoring/${product}/${environment}/services/${encodeURIComponent(service!)}/history`, { hours }),
    enabled: service !== null,
  })
